import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import * as omitBy from 'lodash/omitBy';
import { Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

import { MeQueryParamKey, PaQueryParamKey } from '../constants/query-param.constants';

@UntilDestroy()
@Injectable({
	providedIn: 'root'
})
export class UrlQueryParamService {
	public queryParams: any;

	constructor(
		private route: ActivatedRoute,
		protected router: Router
	) {
		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		this.route.queryParams.subscribe(params => this.updateParams(params));
	}

	protected updateParams(params: Params): void {
		if (isEmpty(params)) {
			this.queryParams = null;
		} else {
			this.queryParams = this.transformParamsToObject(params);
		}
	}

	public transformParamsToObject(params: any): any {
		const obj = {};
		Object.keys(params).forEach(key => {
			try {
				const value = params[key];
				if (value) {
					if (value.match(/^list,.*/)) {
						obj[key] = value
							.split(',')
							.splice(1)
							.map(entry => entry.replace('%2C', ','));
					} else if (value.match(/\[|,|\]/g)) {
						obj[key] = JSON.parse(JSON.stringify(value));
					} else {
						obj[key] = value;
					}
				} else {
					obj[key] = null;
				}
			} catch (err) {
				obj[key] = null;
				console.error(`Can't parse the URL param "${key}"`, params[key]);
			}
		});
		return obj;
	}

	public transformObjectToParams(obj: any): any {
		const params = {};
		if (obj) {
			Object.keys(obj).forEach(key => {
				let encodedVal: string;
				if (typeof obj[key] === 'string' || typeof obj[key] === 'number') {
					encodedVal = obj[key];
				} else if (obj[key] instanceof Array && [...obj[key]].filter(entry => !(typeof entry === 'string' || typeof entry === 'number')).length === 0) {
					encodedVal = obj[key].length === 1 ? obj[key][0] : 'list,' + [...obj[key]].map(entry => String(entry).replace(',', '%2C')).join(',');
				} else {
					encodedVal = JSON.stringify(obj[key]);
				}

				params[key] = encodedVal;
			});
		}
		return params;
	}

	protected getPortfolioIds$(): Observable<string[]> {
		if (get(this.queryParams, 'portfolios')) {
			const ids = this.queryParams.portfolios instanceof Array ? this.queryParams.portfolios : [this.queryParams.portfolios];
			return of(ids);
		} else {
			return of([]);
		}
	}

	/**
	 * Checks if the url contains FamilyIds first
	 * When Url doesn't contain familyId, checks for Portfolio Ids
	 * Returns Observable of [] if no PortfolioIds or FamilyIds are absent
	 * @returns {Observable<string[]>}
	 */
	public getModelFamilyIdsOrPortfolioIds(): Observable<string[]> {
		const familyIds = this.getModelFamilyId();
		if (familyIds) {
			return of([familyIds]);
		}
		return this.portfolioIds$.pipe(first(portfolioIds => !!portfolioIds));
	}

	public get portfolioIds$(): Observable<string[]> {
		return this.route.queryParams.pipe(
			switchMap(params => {
				this.updateParams(params);
				return this.getPortfolioIds$();
			})
		);
	}

	public getEngagementId$(): Observable<string> {
		return this.route.queryParams.pipe(
			map(params => {
				this.updateParams(params);
				return this.getEngagementId();
			})
		);
	}

	public get externalPortfolios$(): Observable<string[]> {
		return this.route.queryParams.pipe(
			switchMap(params => {
				this.updateParams(params);

				if (get(this.queryParams, 'externalPortfolios')) {
					const ids = this.queryParams.externalPortfolios instanceof Array ? this.queryParams.externalPortfolios : [this.queryParams.externalPortfolios];
					return of(ids);
				} else {
					return of([]);
				}
			})
		);
	}

	public get queryParams$(): Observable<any> {
		return this.route.queryParams.pipe(map(queryParams => (queryParams ? this.transformParamsToObject(queryParams) : null)));
	}

	public getFirstPortfolioId(): Array<any> {
		if (this.queryParams?.portfolios) {
			if (this.queryParams.portfolios instanceof Array) {
				return [String(this.queryParams.portfolios[0])];
			} else {
				return [String(this.queryParams.portfolios)];
			}
		}

		return [];
	}

	public getPortfolioIds(): string[] {
		if (this.queryParams?.portfolios) {
			if (this.queryParams.portfolios instanceof Array) {
				return this.queryParams.portfolios;
			} else {
				return [this.queryParams.portfolios];
			}
		}

		return [];
	}

	public getPortfolioMode(): any {
		return this.queryParams ? this.queryParams.mode : '';
	}

	public getParam(param: string): any {
		return this.queryParams ? this.queryParams[param] : '';
	}

	public getAssetClasses(): any {
		const assetClass = get(this.queryParams, 'assetClass', []);
		return assetClass instanceof Array ? assetClass : [assetClass];
	}

	public getStressTests(): any {
		const stressTests = get(this.queryParams, 'stressTest', []);
		return stressTests instanceof Array ? stressTests : [stressTests];
	}

	public getModelFamilyId(): string {
		return get(this.queryParams, 'familyId', '');
	}

	public getModelGroupId(): string {
		return get(this.queryParams, 'groupId', '');
	}

	public getSelectedModelIds(): string {
		return get(this.queryParams, MeQueryParamKey.SELECTED_MODEL_IDS, '');
	}

	public addQueryParamAndReload(paramKey: string, paramValue: string): void {
		const parsedUrl: URL = new URL(window.location.href);
		parsedUrl.searchParams.set(paramKey, paramValue);
		window.location.assign(parsedUrl.href);
	}

	public updateQueryParam(ids: string[], replaceUrl: boolean = false): void {
		const params = this.transformObjectToParams({ portfolios: ids });
		this.navigateByUrl(params, replaceUrl);
	}

	public updateMultipleQueryParams(newParamsToUpdate: { [key: string]: any }, replaceUrl: boolean = false): Promise<boolean> {
		const updatedParam = Object.assign({}, this.queryParams, newParamsToUpdate);
		const validQueryParams = omitBy(updatedParam, value => isNil(value) || value === '');
		const params = this.transformObjectToParams(validQueryParams);
		return this.navigateByUrl(params, replaceUrl);
	}

	public getEngagementId(): string {
		return this.queryParams ? this.queryParams.engagement : this.queryParams;
	}

	public hasSpecificPortfolioId(portfolioId: string): boolean {
		const ids = this.getPortfolioIds();
		return ids && ids.indexOf(portfolioId) > -1;
	}

	public getAcBenchmark(): string {
		return get(this.queryParams, PaQueryParamKey.AC_BENCHMARK, '');
	}

	private navigateByUrl(params: any, replaceUrl): Promise<boolean> {
		const urlTree = this.router.parseUrl(this.router.url);
		const extras: NavigationExtras = {
			replaceUrl: replaceUrl
		};
		urlTree.queryParams = params;

		return this.router.navigateByUrl(urlTree, extras);
	}

}
